<script setup>
import { ref, onMounted } from 'vue';
import AdminLogin from './AdminLogin.vue';
import AdminDashboard from './AdminDashboard.vue';
import PrivacyManager from './PrivacyManager.vue';
import MusicManager from './MusicManager.vue';
import VideoManager from './VideoManager.vue';
import MaintenanceManager from './MaintenanceManager.vue';

// Prüfen, ob der Benutzer als Admin angemeldet ist
const isAdmin = ref(false);
const isLoading = ref(true);
const showPasswordChangeModal = ref(false);
const currentUser = ref({
  username: '',
  passwordChangeRequired: false
});
const passwordChangeSuccess = ref(false);
const passwordChangeMessage = ref('');

// Passwort-Änderungs-Formular
const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});
const passwordError = ref('');
const isChangingPassword = ref(false);

// Prüfen, ob der Benutzer Admin ist
const checkAuth = async () => {
  console.log('Prüfe Admin-Status in AdminArea.vue...');
  const token = localStorage.getItem('adminToken');
  console.log('Admin-Token vorhanden:', !!token);
  
  if (!token) {
    console.log('Kein Token gefunden');
    isAdmin.value = false;
    isLoading.value = false;
    return;
  }

  try {
    console.log('Sende Anfrage an /api/check_admin.php mit Token');
    const response = await fetch('/api/check_admin.php', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    const data = await response.json();
    console.log('API Antwort:', data);
    
    if (data.success && data.authenticated) {
      isAdmin.value = true;
      console.log('Admin-Status: Authentifiziert');
      
      // Prüfen, ob gespeicherte Auth-Daten vorhanden sind
      const authData = JSON.parse(localStorage.getItem('admin_auth') || '{}');
      if (authData.username) {
        currentUser.value = {
          username: authData.username,
          passwordChangeRequired: authData.password_change_required || false
        };
        
        // Prüfen, ob Passwort-Änderung erforderlich ist
        if (authData.password_change_required) {
          console.log('Passwort-Änderung erforderlich (aus gespeicherten Daten)');
          showPasswordChangeModal.value = true;
        }
      }
    } else {
      console.log('Admin-Status: Nicht authentifiziert');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('admin_auth');
      isAdmin.value = false;
    }
  } catch (error) {
    console.error('API-Fehler:', error);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_auth');
    isAdmin.value = false;
  } finally {
    isLoading.value = false;
  }
};

// Beim Laden der Komponente den Admin-Status prüfen
onMounted(() => {
  checkAuth();
});

// Erfolgreicher Login-Handler
const handleLoginSuccess = (authData) => {
  console.log('Login erfolgreich, setze Authentifizierungsstatus');
  console.log('Auth-Daten:', authData);
  
  isAdmin.value = true;
  currentUser.value = {
    username: authData.username,
    passwordChangeRequired: authData.password_change_required
  };
  
  // Prüfen, ob Passwort-Änderung erforderlich ist
  if (authData.password_change_required) {
    console.log('Passwort-Änderung erforderlich, zeige Modal');
    showPasswordChangeModal.value = true;
  }
};

// Passwort ändern
const changePassword = async () => {
  if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
    passwordError.value = 'Die Passwörter stimmen nicht überein.';
    return;
  }
  
  isChangingPassword.value = true;
  passwordError.value = '';
  
  try {
    const token = localStorage.getItem('adminToken');
    const response = await fetch('/api/change_password.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'include',
      body: JSON.stringify({
        current_password: passwordForm.value.currentPassword,
        new_password: passwordForm.value.newPassword
      })
    });
    
    const responseText = await response.text();
    console.log('Passwort-Änderung Antwort (Text):', responseText);
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('JSON Parse Fehler:', parseError);
      console.error('Response Text:', responseText);
      throw new Error('Ungültige Server-Antwort');
    }
    
    console.log('Passwort-Änderung Antwort (JSON):', data);
    
    if (data.success) {
      // Erfolgreiche Passwort-Änderung
      showPasswordChangeModal.value = false;
      passwordChangeSuccess.value = true;
      passwordChangeMessage.value = data.message;
      currentUser.value.passwordChangeRequired = false;
      
      // Auth-Daten aktualisieren
      const authData = JSON.parse(localStorage.getItem('admin_auth') || '{}');
      if (authData) {
        authData.password_change_required = false;
        localStorage.setItem('admin_auth', JSON.stringify(authData));
      }
      
      // Formular zurücksetzen
      passwordForm.value = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      
      // Benachrichtigung nach 5 Sekunden automatisch ausblenden
      setTimeout(() => {
        passwordChangeSuccess.value = false;
      }, 5000);
    } else {
      passwordError.value = data.error || 'Ein Fehler ist aufgetreten.';
    }
  } catch (error) {
    console.error('Fehler beim Ändern des Passworts:', error);
    passwordError.value = 'Ein Netzwerkfehler ist aufgetreten.';
  } finally {
    isChangingPassword.value = false;
  }
};

// Logout-Handler
const handleLogout = async () => {
  try {
    console.log('Sende Logout-Anfrage...');
    const response = await fetch('/api/logout.php', {
      method: 'POST',
      credentials: 'include'
    });
    
    const data = await response.json();
    console.log('Logout-Antwort:', data);
    
    if (data.success) {
      console.log('Logout erfolgreich');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('admin_auth');
      isAdmin.value = false;
      // Zur Startseite weiterleiten
      window.location.href = '/';
    } else {
      console.error('Logout fehlgeschlagen:', data.error);
    }
  } catch (error) {
    console.error('Logout Fehler:', error);
  }
};
</script>

<template>
  <div class="admin-area">
    <!-- Erzwungene Passwort-Änderung Modal -->
    <div v-if="showPasswordChangeModal && isAdmin" class="password-change-overlay">
      <div class="password-change-modal">
        <div class="modal-header">
          <h2>🔒 Passwort-Änderung erforderlich</h2>
          <p class="warning-text">
            Ihr aktuelles Passwort ist zu schwach und muss aus Sicherheitsgründen geändert werden.
          </p>
        </div>
        
        <form @submit.prevent="changePassword" class="password-form">
          <div class="form-group">
            <label for="current-password">Aktuelles Passwort:</label>
            <input 
              type="password" 
              id="current-password"
              v-model="passwordForm.currentPassword" 
              required 
              class="form-input"
              placeholder="Geben Sie Ihr aktuelles Passwort ein"
            />
          </div>
          
          <div class="form-group">
            <label for="new-password">Neues Passwort:</label>
            <input 
              type="password" 
              id="new-password"
              v-model="passwordForm.newPassword" 
              required 
              class="form-input"
              placeholder="Geben Sie ein starkes neues Passwort ein"
            />
          </div>
          
          <div class="form-group">
            <label for="confirm-password">Neues Passwort bestätigen:</label>
            <input 
              type="password" 
              id="confirm-password"
              v-model="passwordForm.confirmPassword" 
              required 
              class="form-input"
              placeholder="Bestätigen Sie Ihr neues Passwort"
            />
          </div>
          
          <!-- Sicherheitstipps -->
          <div class="security-tips">
            <h4>💡 Tipps für ein sicheres Passwort:</h4>
            <ul>
              <li>Mindestens 8 Zeichen lang</li>
              <li>Kombination aus Groß- und Kleinbuchstaben</li>
              <li>Zahlen und Sonderzeichen verwenden</li>
              <li>Keine häufig verwendeten Wörter</li>
              <li>Einzigartig für dieses Konto</li>
            </ul>
          </div>
          
          <div class="form-actions">
            <button 
              type="submit" 
              class="submit-button"
              :disabled="isChangingPassword"
            >
              <span v-if="isChangingPassword">Passwort wird geändert...</span>
              <span v-else>Passwort ändern</span>
            </button>
          </div>
          
          <!-- Fehlermeldungen -->
          <div v-if="passwordError" class="error-message">
            {{ passwordError }}
          </div>
        </form>
        
        <div class="modal-footer">
          <p class="footer-text">
            Diese Änderung ist aus Sicherheitsgründen erforderlich und kann nicht übersprungen werden.
          </p>
        </div>
      </div>
    </div>
    
    <div v-if="isLoading" class="loading">
      Lade...
    </div>
    <template v-else>
      <template v-if="isAdmin">
        <!-- Erfolgreiche Passwort-Änderung Benachrichtigung -->
        <div v-if="passwordChangeSuccess" class="success-notification">
          <div class="notification-content">
            <span class="success-icon">✅</span>
            <div class="notification-text">
              <strong>Passwort erfolgreich geändert!</strong>
              <p>{{ passwordChangeMessage }}</p>
            </div>
            <button @click="passwordChangeSuccess = false" class="close-notification">×</button>
          </div>
        </div>
        
        <div class="dashboard-content" :class="{ 'blurred': showPasswordChangeModal }">
          <AdminDashboard @logout="handleLogout" />
        </div>
      </template>
      <template v-else>
        <AdminLogin @login-success="handleLoginSuccess" />
      </template>
    </template>
  </div>
</template>

<style scoped>
.admin-area {
  min-height: 100vh;
  background-color: var(--bg-color);
  padding: 2rem;
  box-sizing: border-box;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  font-size: 1.2rem;
  color: var(--text-muted);
}

.dashboard-content {
  transition: filter 0.3s ease;
}

.dashboard-content.blurred {
  filter: blur(3px);
  pointer-events: none;
}

/* Passwort-Änderungs-Modal */
.password-change-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.password-change-modal {
  background-color: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  text-align: center;
  margin-bottom: 25px;
}

.modal-header h2 {
  color: #d32f2f;
  margin-bottom: 10px;
  font-size: 24px;
}

.modal-header .warning-text {
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

.password-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #007bff;
}

.security-tips {
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 15px;
  margin: 20px 0;
}

.security-tips h4 {
  margin: 0 0 10px 0;
  color: #1976d2;
  font-size: 16px;
}

.security-tips ul {
  margin: 0;
  padding-left: 20px;
}

.security-tips li {
  margin-bottom: 5px;
  color: #1565c0;
  font-size: 14px;
}

.form-actions {
  margin-top: 25px;
}

.submit-button {
  width: 100%;
  padding: 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.submit-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.error-message {
  color: #f44336;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #ffebee;
  border-radius: 4px;
  border-left: 4px solid #f44336;
}

.modal-footer {
  margin-top: 20px;
  text-align: center;
  border-top: 1px solid #eee;
  padding-top: 15px;
}

.footer-text {
  color: #666;
  font-size: 12px;
  margin: 0;
  font-style: italic;
}

/* Erfolgs-Benachrichtigung */
.success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 15px;
  z-index: 1000;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.success-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.notification-text {
  flex-grow: 1;
}

.notification-text strong {
  color: #155724;
  display: block;
  margin-bottom: 5px;
}

.notification-text p {
  color: #155724;
  margin: 0;
  font-size: 14px;
}

.close-notification {
  background: none;
  border: none;
  font-size: 20px;
  color: #155724;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-notification:hover {
  background-color: rgba(21, 87, 36, 0.1);
  border-radius: 50%;
}
</style> 